import { useEffect, type KeyboardEvent, type PropsWithChildren } from 'react';

import isSaveShortcut from './isSaveShortcut';

type SaveShortcutScopeProps = PropsWithChildren<{
  disabled?: boolean;
  globalShortcut?: boolean;
  onSave: () => unknown;
}>;

function SaveShortcutScope({
  children,
  disabled = false,
  globalShortcut = false,
  onSave,
}: SaveShortcutScopeProps) {
  useEffect(() => {
    if (!globalShortcut) {
      return;
    }

    const handleGlobalKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented || !isSaveShortcut(event)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (disabled || event.repeat || event.isComposing) {
        return;
      }

      void onSave();
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [disabled, globalShortcut, onSave]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isSaveShortcut(event)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (disabled || event.repeat || event.nativeEvent.isComposing) {
      return;
    }

    void onSave();
  };

  return (
    // This wrapper handles shortcuts from its descendants and is not itself
    // an interactive element, so assigning an ARIA role would be misleading.
    // biome-ignore lint/a11y/noStaticElementInteractions: keyboard event boundary
    <div
      style={{ display: 'contents' }}
      onKeyDown={globalShortcut ? undefined : handleKeyDown}
    >
      {children}
    </div>
  );
}

export default SaveShortcutScope;
